/**
 * RUF-555 — pure display-mapping helpers for the digital passport, design
 * docs/prds/digital-passport-health-confidence.md.
 *
 * Moved here from apps/customer/src/lib/passport.ts (RUF-556) so the
 * shared PDF render component (./PassportDocument.tsx, used by the
 * apps/passport-render Lambda) can reuse the EXACT same copy/formatting
 * rules the interactive passport page uses — the PDF and the web page must
 * never drift on wording. apps/customer/src/lib/passport.ts now just
 * re-exports these.
 */

import type { ConfidenceTier } from '../scoring/confidence-tier.js';
import type { HorizonBandInfo } from '../scoring/horizon-band.js';
import type { TelemetryDisclosure } from '../scoring/telemetry-disclosures.js';
import type { UsePhaseNotComputedReason } from '../dto/use-phase.js';
import type { ProvenanceEventClient, ProvenanceEventType } from '../dto/provenance.js';
import type {
  PassportWarranty,
  StatusTransitionEventClient,
  TelemetryPassportClientResponse,
} from '../dto/telemetry-passport.js';

// ---------------------------------------------------------------------------
// §1 Identity & Coverage — confidence tier
// ---------------------------------------------------------------------------

/**
 * Tier-naming note (RUF-555): the engine's confidence tier values are
 * full/partial/minimal, but portal copy displays "Full History" /
 * "Partial History" / "Limited History" (minimal -> "Limited History") per
 * the PRD. confidenceTierLabel() in this package already returns exactly
 * this — this map exists only for callers that need it without importing
 * the shared function name directly (kept for symmetry/documentation).
 */
export const CONFIDENCE_TIER_DISPLAY_LABEL: Record<ConfidenceTier, string> = {
  full: 'Full History',
  partial: 'Partial History',
  minimal: 'Limited History',
};

/**
 * Anti-laundering framing (design D5/D6): for partial/minimal tiers, copy
 * must read as "not yet proven" rather than negative/accusatory. Full tier
 * gets no disclosure line — the history speaks for itself.
 */
export function confidenceDisclosureLine(tier: ConfidenceTier): string | null {
  if (tier === 'full') return null;
  if (tier === 'partial') {
    return 'This asset has a partial telemetry history — health signals here are real but not yet proven over a long observation window.';
  }
  return 'This asset has a limited telemetry history — health signals here are real but not yet proven; treat with extra diligence until more history accrues.';
}

// ---------------------------------------------------------------------------
// §2 Health & Operational Fitness — horizon band fitness wording
// ---------------------------------------------------------------------------

/**
 * "{label} — {meaning}" — horizonBandInfo() is the shared, reused-verbatim
 * source of truth for label + operational-fitness meaning (design D4); this
 * only composes the passport's single display line from it.
 */
export function fitnessLine(info: HorizonBandInfo): string {
  return `${info.label} — ${info.meaning}`;
}

// ---------------------------------------------------------------------------
// §3 Reliability Dossier — damage-budget headroom
// ---------------------------------------------------------------------------

/** Headroom bar fill (0-100), scaled to the metric's own critical threshold. */
export function damageBudgetHeadroomPct(value: number, criticalThreshold: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(criticalThreshold) || criticalThreshold <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(1, value / criticalThreshold)) * 100;
}

/** "Row remaps: 2 — concern threshold 10" */
export function formatDamageBudgetCaption(
  label: string,
  value: number,
  criticalThreshold: number,
): string {
  return `${label}: ${formatDamageBudgetValue(value)} — concern threshold ${formatDamageBudgetValue(criticalThreshold)}`;
}

function formatDamageBudgetValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

// ---------------------------------------------------------------------------
// §4 Operational disclosures — plain-language disclosure copy
// ---------------------------------------------------------------------------

export const DISCLOSURE_LABEL: Record<TelemetryDisclosure, string> = {
  thermal_stress_exposure: 'Thermal Stress Exposure',
  fabric_instability: 'Fabric Instability',
  high_duty_cycle: 'High-Duty Cycle',
};

export const DISCLOSURE_EXPLANATION: Record<TelemetryDisclosure, string> = {
  thermal_stress_exposure:
    'This unit has recorded a thermal shutdown, or throttle/violation events beyond the model’s normal-operation threshold.',
  fabric_instability:
    'This unit has recorded a PCIe/NVLink link-down or fallen-off-bus event beyond the model’s normal-operation threshold.',
  high_duty_cycle:
    'This unit has run sustained near-max utilization across most of its observed history — informational, not an accusation of damage.',
};

export function disclosureLabel(disclosure: TelemetryDisclosure): string {
  return DISCLOSURE_LABEL[disclosure];
}

export function disclosureExplanation(disclosure: TelemetryDisclosure): string {
  return DISCLOSURE_EXPLANATION[disclosure];
}

/** What the passport checked for when an asset carries no disclosures (RUF-555:
 * a positive "No disclosures on record" state must list what was checked, not
 * just say "nothing found"). */
export const DISCLOSURES_CHECKED: readonly TelemetryDisclosure[] = [
  'thermal_stress_exposure',
  'fabric_instability',
  'high_duty_cycle',
];

/**
 * One-line explanation of WHY no Scope 2 figure exists, for the passport's
 * environmental section. Shared by the interactive page and the PDF
 * (PassportDocument.tsx) so the two never drift on wording.
 *
 * The customer asset-detail `UsePhaseCard` renders the same three reasons at
 * more length with distinct icons/colors; the passport is a dense document, so
 * these are the same substance compressed to a sentence. All three stay
 * visibly distinct in meaning: a newly-onboarded asset must not read as broken
 * software, and only `unsupported_asset_class` may read as "not applicable".
 */
export function usePhaseNotComputedLine(reason: UsePhaseNotComputedReason): string {
  switch (reason) {
    case 'pending_first_computation':
      return 'Pending first computation — this asset is newly onboarded and has not yet been through a monthly operational-emissions run.';
    case 'no_archetype_without_telemetry':
      return 'Awaiting telemetry — this hardware type has no default operational-emissions estimate without measured power draw, so no figure is modeled until telemetry is connected.';
    case 'unsupported_asset_class':
      return 'Not applicable — operational emissions are not calculated for this asset’s hardware class.';
  }
}

// ---------------------------------------------------------------------------
// Serial masking (SIL-824 adjacent) — the passport must PROVE a serial is on
// file without publishing it. Every passport surface renders the serial
// through this helper; no surface prints `identity.serialNumber` raw.
// ---------------------------------------------------------------------------

/** Rendered where an asset has no serial on file at all. */
export const SERIAL_WITHHELD_TEXT = 'Not on file';

/** Characters of the serial the passport publishes. */
const SERIAL_VISIBLE_CHARS = 4;

/**
 * "PXN4---" — the leading characters (manufacturer prefix / batch marker, the
 * part a reader can sanity-check against the hardware in front of them)
 * followed by a FIXED three hyphens.
 *
 * ponytail: the hyphen run is fixed at three and never scaled to the hidden
 * remainder — a proportional mask would leak the serial's length, which
 * narrows a guess against a manufacturer's known serial format. A serial of
 * 8 characters or fewer publishes at most half of itself, so a short serial
 * cannot be reconstructed from its own mask.
 */
export function maskSerial(serial: string): string {
  const trimmed = serial.trim();
  if (trimmed.length === 0) return SERIAL_WITHHELD_TEXT;
  const visible = Math.min(SERIAL_VISIBLE_CHARS, Math.ceil(trimmed.length / 2));
  return `${trimmed.slice(0, visible)}---`;
}

/** Label + note for a serial the owner typed by hand (asset.user_provided_serial, 929). */
export const USER_PROVIDED_SERIAL_LABEL = 'Serial (user provided, unverified)';
export const USER_PROVIDED_SERIAL_NOTE =
  'This serial was entered by the owner and has not been verified against the hardware. It does not establish the asset’s Fingerprint.';

/** Accompanies every masked serial — a reader must not read the mask as damage. */
export const SERIAL_MASK_NOTE =
  'The serial number is masked. Only the leading characters are published; the full serial is disclosed to the buyer at settlement.';

// ---------------------------------------------------------------------------
// Provenance / lifecycle event copy (SIL-821) — lives here, not in a
// renderer, because BOTH the interactive passport page
// (ui/AssetPassportBody.tsx) and the printed document
// (passport/PassportDocument.tsx) render these events. One copy, no drift.
// ---------------------------------------------------------------------------

export const PROVENANCE_EVENT_LABEL: Record<ProvenanceEventType, string> = {
  creation: 'Identity established',
  transfer: 'Ownership transferred',
  disclosure: 'Serial disclosed to a party',
  merge: 'Duplicate record merged',
  split: 'Component split into its own asset',
  dispute: 'Identity dispute',
};

export function provenanceEventDescription(event: ProvenanceEventClient): string {
  // SIL-823: counterpartyOrgHolder is a per-chain alias ("Verified Holder
  // A7F3") unless that org opted in to show its legal name — safe to show
  // any viewer, never the raw org name/id.
  switch (event.eventType) {
    case 'transfer':
      return event.counterpartyOrgHolder ? `Transferred to ${event.counterpartyOrgHolder}` : 'Ownership transferred';
    case 'disclosure':
      return event.counterpartyOrgHolder
        ? `Serial disclosed to ${event.counterpartyOrgHolder}`
        : 'Serial disclosed to a party';
    case 'dispute':
      return event.disputePhase === 'resolved' ? 'Dispute resolved' : 'Dispute opened';
    default:
      return PROVENANCE_EVENT_LABEL[event.eventType];
  }
}

/**
 * Status-transition copy (asset_lifecycle_event, event_type='lifecycle_state'
 * — db/schema/498_asset_lifecycle_advance.sql / 678). Keyed by `toState`
 * (the asset_lifecycle_state id it landed in — 010_reference_tables.sql /
 * 200_seed_reference.sql / 451_exchange_management.sql), never `fromState`:
 * the state the asset landed IN is what the reader cares about. A toState
 * with no entry here (a future asset_lifecycle_state added without a
 * matching label) falls back to a generic "Status changed to <state>" rather
 * than throwing or silently omitting the milestone.
 */
export const STATUS_TRANSITION_TO_STATE_LABEL: Record<string, string> = {
  active: 'Returned to active use',
  idle: 'Marked idle',
  decommissioned: 'Decommissioned',
  in_transit: 'Moved in transit',
  disposed: 'Disposed',
  quarantined: 'Quarantined',
  sold: 'Sold',
};

export function statusTransitionDescription(event: StatusTransitionEventClient): string {
  if (!event.toState) return 'Status changed';
  return STATUS_TRANSITION_TO_STATE_LABEL[event.toState] ?? `Status changed to ${event.toState}`;
}

// ---------------------------------------------------------------------------
// Asset lifecycle — the asset's dated history in one place: the hash-chained
// custody events (SIL-821) merged with the telemetry observation window and
// the Active Verification run.
//
// Lives here, not in PassportDocument.tsx, for the reason in this file's
// header: the issued PDF and the interactive page must never drift. It was
// document-only until the in-app Passport gained the same section, and one
// surface silently lacking the asset's dated history is exactly the drift
// this module exists to prevent.
//
// ponytail: derived from the payload the passport already carries — no new
// DTO field, no new query. A milestone with no date on record is omitted
// rather than printed as "unknown".
// ---------------------------------------------------------------------------

export interface LifecycleMilestone {
  date: string;
  label: string;
}

/** Deterministic: sorted by date, ties broken by insertion order (a stable
 * sort), so the same payload always renders the same bytes for hashing. */
export function lifecycleMilestones(
  passport: TelemetryPassportClientResponse,
): LifecycleMilestone[] {
  const { coverage, provenance, statusTransitions, verification } = passport;
  const milestones: LifecycleMilestone[] = provenance.map((event) => ({
    date: event.occurredAt,
    label: provenanceEventDescription(event),
  }));
  for (const event of statusTransitions) {
    milestones.push({ date: event.occurredAt, label: statusTransitionDescription(event) });
  }
  if (coverage.firstSeen) {
    milestones.push({ date: coverage.firstSeen, label: 'Telemetry monitoring began' });
  }
  if (verification.kind !== 'none') {
    milestones.push({ date: verification.completedAt, label: 'Active Verification run completed' });
  }
  if (coverage.lastSeen) {
    milestones.push({ date: coverage.lastSeen, label: 'Most recent telemetry observation' });
  }
  return milestones.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

// ---------------------------------------------------------------------------
// SIL-1919 - Manufacturer Entitlement Record (OEM warranty data as reported)
//
// Wording is shared by the PDF (PassportDocument) and the customer page so the
// two never drift. Dates only: no "in warranty" / "out of warranty" status, no
// badge, no days-remaining, no colour. The OEM's own dates, attributed.
// ---------------------------------------------------------------------------

export const WARRANTY_SECTION_HEADING = 'Manufacturer Entitlement Record';

const WARRANTY_SOURCE_LABELS: Record<string, string> = {
  'dell-techdirect': 'Dell TechDirect',
};

/** Unknown adapter keys render the raw key; they never throw. */
export function warrantySourceLabel(sourceAdapter: string): string {
  return WARRANTY_SOURCE_LABELS[sourceAdapter] ?? sourceAdapter;
}

/** "Reported by Dell TechDirect on 2026-10-14." */
export function warrantyLeadLine(warranty: PassportWarranty): string {
  return `Reported by ${warrantySourceLabel(warranty.sourceAdapter)} on ${warranty.fetchedAt.slice(0, 10)}.`;
}

/**
 * Prose lines under the lead line. Asset scope (owner) is rendered as a ship
 * date + entitlement table by the caller, so it contributes no prose here.
 */
export function warrantySummaryLines(warranty: PassportWarranty): string[] {
  const lines: string[] = [];
  const line = warranty.scope === 'stock_line';
  if (line) {
    lines.push(`${warranty.unitsWithRecord} of ${warranty.quantity} units have a manufacturer record.`);
  }
  if (warranty.projection === 'buyer') {
    if (warranty.latestEndDate) lines.push(`Latest entitlement end reported: ${warranty.latestEndDate}.`);
  } else if (line && warranty.earliestEndDate && warranty.latestEndDate) {
    lines.push(`Entitlements end between ${warranty.earliestEndDate} and ${warranty.latestEndDate}.`);
  }
  if (warranty.serviceLevels.length > 0) lines.push(`Service levels: ${warranty.serviceLevels.join(', ')}.`);
  if (line && warranty.projection === 'owner' && warranty.unitsWithRecord < warranty.quantity) {
    const missing = warranty.quantity - warranty.unitsWithRecord;
    lines.push(`${missing} unit${missing === 1 ? ' has' : 's have'} no record.`);
  }
  return lines;
}
